import React from 'react';
import { func, object, string, bool } from 'prop-types';
import classNames from 'classnames';
// Import configs and util modules
import { FormattedMessage } from '../../../../util/reactIntl';
import { CATEGORY_BRIDAL_GOWNS, CATEGORY_GOWNS, LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_PUBLISHED } from '../../../../util/types';
// Import shared components
import { AvatarSmall, Button, H2, IconCollection, InlineTextButton, NamedLink } from '../../../../components';
import css from './EditListingPreviewPanel.module.css';

import SectionGallery from '../../../ListingPage/SectionGallery';
import { ensureUser, userDisplayNameAsString } from '../../../../util/data';
import { capitalizeEachWord } from '../../../../util/dataExtractor';
import { ExpandableBio } from '../../../ListingPage/UserCard/UserCard';
import IconCard from '../../../../components/SavedCardDetails/IconCard/IconCard';

const EditListingPreviewPanel = props => {

  const { className, rootClassName, listing, updateInProgress, handlePublishListing, currentUser, config, intl, onPrevTab } = props;
  const classes = classNames(rootClassName || css.root, className);
  const isPublished = [LISTING_STATE_PUBLISHED].includes(listing?.id && listing?.attributes.state)
  const isPendingApproval = [LISTING_STATE_PENDING_APPROVAL].includes(listing?.id && listing?.attributes.state);

  const { publicData, title, description, price } = listing?.attributes || {};
  const {
    category,
    size,
    streetSize,
    heelHeight,
    style,
    material,
    color,
    brand,
    anyDamage,
    originalPrice,
    length,
    neckline,
    sleeves,
    productType,
    designer,
    purchasedYear,
    yearPurchased,
    originalOwner,
    originalReceipt,
    allowNegotiation,
    termsOfSale,
    alterations
  } = publicData || {};

  const { isOriginalOwner, isInvoiceAvailable, allowOffers } = termsOfSale || {};
  const authorAvailable = listing && listing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing = userAndListingAuthorAvailable && listing.author.id.uuid === currentUser.id.uuid;

  const currentAuthor = authorAvailable ? listing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);

  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const contact =
    <InlineTextButton
      rootClassName={css.contact}
      enforcePagePreloadFor="SignupPage"
    >
      <FormattedMessage id="UserCard.contactUser" />
    </InlineTextButton>

  const makeAnOfferButton = (
    <div className={css.makeAnOfferButton}>
      <Button className={classNames(css.removedCard, css.removeCard)}>
        <IconCollection icon="makeAnOffer" />
        <FormattedMessage id="ListingPage.makeAnOffer" />
      </Button>
    </div>
  );

  const addToBagAndRemoveFromBag = (
    <div className={css.addToCartBtn}>
      <Button className={classNames(css.removeCard)}>
        {<IconCollection icon={'addToBag'} />}{' '}
        <FormattedMessage id={'ListingPage.addToBag'} />
      </Button>
    </div>
  );

  return (
    <div className={classes}>
      <div className={css.breadCrumb}>
        <span className={css.breadCrumbLink}><FormattedMessage id={'AlgoliaSearchPage.homePage'} /></span>
        <span className={css.divider}>/</span>
        <span className={css.breadCrumbLink}>{capitalizeEachWord(productType)}</span>
        <span className={css.divider}>/</span>
        <span className={css.breadCrumbLink}>{title}</span>
      </div>
      <div className={css.container}>
        <SectionGallery
          currentUser={currentUser}
          listing={listing}
          variantPrefix={config.layout.listingImage.variantPrefix}
          isOwnListing={isOwnListing}
          intl={intl}
        />
        <div className={css.details}>
          <div className={css.section}>
            <h1 className={css.text}>{title}</h1>
            <div>
              <div className={css.originalPriceText}>
                <FormattedMessage
                  id="EditListingPreviewPanel.retailPrice"
                  values={{ retailPrice: <b> {(originalPrice).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</b> }}
                />
              </div>

              <div className={css.askingPriceText}>
                <FormattedMessage
                  id="EditListingPreviewPanel.askingPrice"
                  values={{ askingPrice: <b>{(price?.amount / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</b> }}
                />
              </div>
            </div>

            <div className={css.author}>
              <AvatarSmall user={ensuredAuthor} className={css.providerAvatar} />
              <div>
                <div className={css.providerNameLinked}>
                  <FormattedMessage id="OrderPanel.author" values={{ name: authorDisplayName }} />
                </div>
                <span className={css.contactBtn}><IconCollection icon="MESSAGE_ICON" />{" "}{contact}</span>
              </div>
            </div>
            <ExpandableBio className={css.description} bio={description} />
            <div className={css.buttonGroups}>
              {makeAnOfferButton}
              {addToBagAndRemoveFromBag}
            </div>
            <div className={css.submitBuyButton}>
              <Button>
                <IconCollection icon="ICON_WHITE_BAG" />
                <FormattedMessage id="OrderPanel.ctaButtonMessagePurchase" />
              </Button>
            </div>

            <H2 className={css.productDetailsTitle}>
              <FormattedMessage id="EditListingWizard.tabLabelProductDetails" />
            </H2>


          </div>
          <div className={css.productDetailsContents}>
            {
              (brand && typeof (brand) == "string") ? <span className={classNames(css.categoryText, css.categoryTextBrand)}>
                <FormattedMessage
                  id="EditListingPreviewPanel.brand"
                  values={{ brand: <b>{brand}</b> }}
                />
              </span> : null
            }
            {size ? (
              <>
                <span className={css.categoryText}>
                  <FormattedMessage
                    id={[CATEGORY_GOWNS, CATEGORY_BRIDAL_GOWNS].includes(productType) ? "EditListingPreviewPanel.bridalSize" : "EditListingPreviewPanel.size"}
                    values={{ size: <b>{size ?? size}</b> }}
                  />{' '}
                </span>
              </>
            ) : null}

            {color ? (
              <>
                <span className={css.categoryText}>
                  <FormattedMessage
                    id="EditListingPreviewPanel.color"
                    values={{ color: <b>{color ?? color}</b> }}
                  />{' '}
                </span>
              </>
            ) : null}
            {streetSize ? (
              <>
                <span className={css.categoryText}>
                  <FormattedMessage
                    id="EditListingPreviewPanel.streetSize"
                    values={{ streetSize: <b>{streetSize}</b> }}
                  />{' '}
                </span>
              </>
            ) : null}
            {heelHeight ? (
              <>
                <span className={css.categoryText}>
                  <FormattedMessage
                    id="EditListingPreviewPanel.heelHeight"
                    values={{ heelHeight: <b>{heelHeight}</b> }}
                  />{' '}
                </span>
              </>
            ) : null}

            {designer ? (
              <>
                <span className={css.categoryText}>
                  <FormattedMessage
                    id="EditListingPreviewPanel.designer"
                    values={{ designer: <b>{designer ?? designer}</b> }}
                  />{' '}
                </span>
              </>
            ) : null}
            {neckline ? (
              <>
                <span className={css.categoryText}>
                  <FormattedMessage
                    id="EditListingPreviewPanel.necline"
                    values={{ neckline: <b>{neckline}</b> }}
                  />{' '}
                </span>
              </>
            ) : null}
            {length ? (
              <>
                <span className={css.categoryText}>
                  <FormattedMessage
                    id="EditListingPreviewPanel.length"
                    values={{ length: <b>{length}</b> }}
                  />{' '}
                </span>
              </>
            ) : null}
            {sleeves ? (
              <>
                <span className={css.categoryText}>
                  <FormattedMessage
                    id="EditListingPreviewPanel.sleeves"
                    values={{ sleeves: <b>{sleeves}</b> }}
                  />{' '}
                </span>

              </>
            ) : null}
            {material ? (
              <>
                <span className={css.categoryText}>
                  <FormattedMessage
                    id="EditListingPreviewPanel.material"
                    values={{ material: <b>{material}</b> }}
                  />{' '}
                </span>

              </>
            ) : null}

            {(Array.isArray(style) && style?.length > 0) ? (
              <span className={css.categoryText}>
                <FormattedMessage
                  id="EditListingPreviewPanel.styleArray"
                />
                {style.map((e, index) => (
                  <span key={index}>
                    <b>{e}{index !== style.length - 1 ? ',' : ''}</b>
                  </span>
                ))}
              </span>)
              : style ? (
                <>
                  <span className={css.categoryText}>
                    <FormattedMessage
                      id="EditListingPreviewPanel.style"
                      values={{ style: <b>{style}</b> }}
                    />{' '}
                  </span>

                </>
              ) : null}

            {
              (purchasedYear || yearPurchased) ? (<span className={css.categoryText}>
                <FormattedMessage
                  id="EditListingPreviewPanel.purchasedYear"
                  values={{ purchasedYear: <b>{purchasedYear || yearPurchased}</b> }}
                />{' '}
              </span>) : null
            }
            {
              originalOwner ? (<span className={css.categoryText}>
                <FormattedMessage
                  id="OrderPanel.originalOwner"
                  values={{ originalOwner: <b>{(originalOwner || isOriginalOwner)?.label}</b> }}
                />
              </span>) : null
            }

            {
              originalReceipt ? (<span className={css.categoryText}>
                <FormattedMessage
                  id="OrderPanel.originalReceiptAvailable"
                  values={{ originalReceiptAvailable: <b>{(originalReceipt || isInvoiceAvailable)?.label}</b> }}
                />
              </span>) : null
            }

            {
              allowNegotiation ? (<span className={css.categoryText}>
                <FormattedMessage
                  id="OrderPanel.negotiation"
                  values={{ negotiation: <b>{allowNegotiation ? allowNegotiation?.charAt(0).toUpperCase() + allowNegotiation?.slice(1) : allowOffers ? "Allowed" : "No"} </b> }}
                />
              </span>) : null
            }
            {
              alterations?.trim() ? (
                <span className={classNames(css.categoryText, css.categoryTextBrand)}>
                  <FormattedMessage
                    id="EditListingPreviewPanel.alterations"
                    values={{ alterations: <b>{alterations}</b> }}
                  />
                </span>
              ) : null
            }
            {
              anyDamage?.trim() ? (
                <span className={classNames(css.categoryText, css.categoryTextBrand)}>
                  <FormattedMessage
                    id="EditListingPreviewPanel.anyDamage"
                    values={{ anyDamage: <b>{anyDamage}</b> }}
                  />
                </span>
              ) : null
            }
          </div>

          <div className={css.bottomStickyWrapper}>
            <div>
              <div className={css.reviewingText}><FormattedMessage id="EditListingPreviewPanel.reviewing" /></div>

              <div className={css.reviewingTitle}>{title}</div>
            </div>
            <div className={css.rightContents}>
              <div className={css.backToEdit} onClick={onPrevTab}>
                <IconCard brand='backicon' />
                <FormattedMessage id="EditListingPreviewPanel.backToEdit" />
              </div>

              {isPublished ? (
                <NamedLink name="ManageListingsPage">
                  <div>
                    <Button className={css.submitButton} type="submit" inProgress={updateInProgress}>
                      <FormattedMessage id="EditListingPreviewPanel.cancelButton" />
                    </Button>
                  </div>
                </NamedLink>
              ) : isPendingApproval ? <NamedLink name="ManageListingsPage">
                <div>
                  <Button className={css.submitButton} type="submit" inProgress={updateInProgress}>
                    <FormattedMessage id="EditListingPreviewPanel.publishButton" />
                  </Button>
                </div>
              </NamedLink> : (
                <div
                  onClick={() => {
                    handlePublishListing(listing.id);
                  }}
                >
                  <Button type="button" className={css.submitButton} inProgress={updateInProgress}>
                    <FormattedMessage id="EditListingPreviewPanel.publishButton" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

EditListingPreviewPanel.defaultProps = {
  className: null,
  rootClassName: null,
  listing: null,
};

EditListingPreviewPanel.propTypes = {
  className: string,
  rootClassName: string,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: object,
  marketplaceCurrency: string.isRequired,

  disabled: bool.isRequired,
  ready: bool.isRequired,
  onSubmit: func.isRequired,
  submitButtonText: string.isRequired,
  panelUpdated: bool.isRequired,
  updateInProgress: bool.isRequired,
  errors: object.isRequired,
  // from useConfiguration
  config: object.isRequired,
};

export default EditListingPreviewPanel;
